const WIDGET_SRC = "https://assets.calendly.com/assets/external/widget.js";
const WIDGET_CSS = "https://assets.calendly.com/assets/external/widget.css";

type CalendlyApi = {
  initPopupWidget: (options: { url: string }) => void;
};

declare global {
  interface Window {
    Calendly?: CalendlyApi;
  }
}

let widgetPromise: Promise<void> | null = null;

function loadWidget(): Promise<void> {
  if (window.Calendly) return Promise.resolve();
  if (widgetPromise) return widgetPromise;

  widgetPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${WIDGET_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = WIDGET_CSS;
      document.head.appendChild(link);
    }

    const existing = document.querySelector(`script[src="${WIDGET_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Calendly failed to load")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = WIDGET_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Calendly failed to load"));
    document.body.appendChild(script);
  });

  return widgetPromise;
}

export type CalendlyPrefill = { name?: string; email?: string };

/** Calendly reads `name` and `email` query params to pre-fill the booking form. */
function withPrefill(url: string, prefill?: CalendlyPrefill): string {
  if (!prefill?.name && !prefill?.email) return url;
  const target = new URL(url);
  if (prefill.name) target.searchParams.set("name", prefill.name);
  if (prefill.email) target.searchParams.set("email", prefill.email);
  return target.toString();
}

/** Opens the Calendly popup. Falls back to a new tab if the widget cannot load. */
export async function openCalendlyPopup(
  baseUrl: string,
  prefill?: CalendlyPrefill,
): Promise<void> {
  const url = withPrefill(baseUrl, prefill);
  try {
    await loadWidget();
    if (!window.Calendly) throw new Error("Calendly unavailable");
    window.Calendly.initPopupWidget({ url });
  } catch {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

/**
 * Calls `onBooked` when a visitor completes a booking in the Calendly popup.
 * Calendly posts `calendly.event_scheduled` to the parent window.
 */
export function onCalendlyBooked(onBooked: () => void): () => void {
  function handle(event: MessageEvent) {
    if (!/^https:\/\/([a-z0-9-]+\.)?calendly\.com$/.test(event.origin)) return;
    const data = event.data as { event?: unknown } | null;
    if (data && data.event === "calendly.event_scheduled") onBooked();
  }
  window.addEventListener("message", handle);
  return () => window.removeEventListener("message", handle);
}
